import React, { useEffect, useRef, useState } from "react";
import { FaTimes } from "react-icons/fa";
import customFetch from "../../util/config/axiosConfig";
import { getDataUserId } from "../../util/manageAuth";
import { whtsappIcon } from "../../asset";
import { ChatMessage, UIMessage } from "../../types/messageType";

interface Props {
    open: boolean;
    onClose: () => void;
    phone: string;
    whatsappBg: string;
}



export default function ChatModal({
    open,
    onClose,
    phone,
    whatsappBg,
}: Props) {

    const [messages, setMessages] = useState<UIMessage[]>([]);
    const bottomRef = useRef<HTMLDivElement | null>(null);

    // Fetch messages
    const fetchChatMessages = async () => {
        try {
            const userId = getDataUserId();

            const response = await customFetch.get(
                `/api/whatsapp/send/whatsapp/conversations/search`,
                {
                    params: {
                        userId,
                        mobile: phone,
                        page: 0,
                        size: 50,
                    },
                }
            );

            let payload = response.data;

            if (typeof payload?.data === "string") {
                payload.data = JSON.parse(payload.data);
            }

            const formatted: UIMessage[] = payload.data.results
                .map((msg: ChatMessage) => ({
                    id: msg.id,
                    text: msg.message,
                    type: msg.direction === "OUTBOUND" ? "sent" : "received",
                    time: new Date(msg.timestamp).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                    }),
                }))
                .sort((a: UIMessage, b: UIMessage) => a.id - b.id);

            setMessages(formatted);

        } catch (error) {
            console.error("Failed to fetch chat messages", error);
        }
    };

    // Fetch when modal opens
    useEffect(() => {
        if (open && phone) {
            fetchChatMessages();
        }
    }, [open, phone]);

    // Lock body scroll
    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }

        return () => {
            document.body.style.overflow = "auto";
        };
    }, [open]);

    // Auto scroll bottom
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex justify-end bg-web-background/30 overflow-hidden">
            <div className="w-[500px] h-full bg-white shadow-xl flex flex-col">

                {/* Header */}
                <div className="flex items-start justify-between px-3 py-4 bg-white border-b border-gray-100">

                    <div className="flex items-center gap-3">
                        <img
                            src={whtsappIcon}
                            className="w-5 h-5"
                            alt="WhatsApp"
                        />

                        <span className="font-semibold text-black">
                            {phone}
                        </span>
                    </div>

                    <FaTimes
                        onClick={onClose}
                        className="text-gray-500 cursor-pointer hover:text-gray-700 text-sm mt-1"
                    />
                </div>

                {/* Chat Body */}
                <div
                    className="flex-1 overflow-y-auto p-4 space-y-3 bg-cover bg-center"
                    style={{ backgroundImage: `url(${whatsappBg})` }}
                >
                    {messages.map((msg) => (
                        <div
                            key={msg.id}
                            className={`flex ${msg.type === "sent"
                                    ? "justify-end"
                                    : "justify-start"
                                }`}
                        >
                            <div
                                className={`max-w-[70%] px-3 py-2 rounded-lg text-sm text-black shadow ${msg.type === "sent"
                                        ? "bg-[#E4FEE0]"
                                        : "bg-white"
                                    }`}
                            >
                                <div className="whitespace-pre-line">
                                    {msg.text}
                                </div>

                                <div className="text-[10px] text-gray-500 text-right mt-1">
                                    {msg.time}
                                </div>
                            </div>
                        </div>
                    ))}

                    <div ref={bottomRef} />
                </div>

            </div>
        </div>
    );
}